import { nanoid } from 'nanoid'
import type { OrthographicCamera, PerspectiveCamera } from 'three'
import { Frustum, Group, Matrix4, Vector3 } from 'three'
import type { GraphicsRequirements } from '@/graphics/GraphicsRequirements'
import { TilesetJsonParser } from '@/plugins/tiles/adapters/TilesetJsonParser'
import type { IAssetSource, TileContentPrepareContext } from '@/plugins/tiles/contracts'
import { resolveTilesRuntimeOptions } from '@/plugins/tiles/runtime/options'
import { TileCacheBudget } from '@/plugins/tiles/runtime/TileCacheBudget'
import { TilePreparationCoordinator } from '@/plugins/tiles/runtime/TilePreparationCoordinator'
import { TileScheduler } from '@/plugins/tiles/runtime/TileScheduler'
import { TilesCollisionRuntime } from '@/plugins/tiles/runtime/TilesCollisionRuntime'
import { TilesRuntime } from '@/plugins/tiles/runtime/TilesRuntime'
import { classifyTileResource } from '@/plugins/tiles/sources/classifyTileResource'
import { createResourceContext, HttpAssetSource } from '@/plugins/tiles/sources/HttpAssetSource'
import {
  createTilesReference,
  validateSourceBounds,
  validateTilesTransform,
} from '@/plugins/tiles/spatial/tilesSpatial'
import { TilesError, throwIfTilesAborted, toTilesDiagnostic } from '@/plugins/tiles/TilesError'
import { TilesetRecord } from '@/plugins/tiles/TilesetRecord'
import type {
  ITilesService,
  TilesAdaptiveBudgetOptions,
  TilesAppearance,
  TilesCollisionCoverage,
  TilesCollisionInterest,
  TilesetHandle,
  TilesLoadRequest,
  TilesMemoryBytes,
  TilesMemoryStatus,
  TilesPluginOptions,
  TilesRuntimeOptions,
  TilesStatus,
} from '@/plugins/tiles/types'
import type { EngineContext } from '@/runtime/EngineContext'
import type { ILogger } from '@/shared/logging'
import type { PhaseRenderContext } from '@/shared/types/assets'
import { RenderPhase } from '@/shared/types/assets'
import type { AssetId, PickQuery, PickResult } from '@/shared/types/core'
import type { AssetPlugin } from '@/shared/types/plugin'
import type {
  RenderActivityContext,
  RenderActivityReport,
  RenderPipeline,
  ViewRegistry,
} from '@/shared/types/services'
import type { EngineFrameContext, ViewFrameContext } from '@/shared/types/view'

/** 显式 1.1 静态 Mesh，按视图提交；内容能力由宿主注入。 */
export class TilesPlugin implements AssetPlugin<TilesLoadRequest, TilesetHandle>, ITilesService {
  readonly name = 'tiles'
  readonly kind = 'tiles' as const
  /** Mesh Tiles 经 backend-neutral 适配器在 WebGL2/WebGPU 双端提交，依赖表面渲染与多视图。 */
  readonly graphicsRequirements: GraphicsRequirements = Object.freeze({
    backends: Object.freeze(['webgl2', 'webgpu'] as const),
    capabilities: Object.freeze(['surfaceRender', 'multiView'] as const),
  })
  readonly priority = 40
  /** 点云默认/EDL 输出颜色和深度后再绘制 Tiles，避免被点云清屏擦除。 */
  readonly renderPriority = 5
  readonly phases = [RenderPhase.MainOpaque, RenderPhase.MainTransparent] as const
  readonly dependencies = ['RenderPipeline', 'ViewRegistry'] as const
  readonly provides = ['TilesService'] as const
  private readonly _records = new Map<AssetId, TilesetRecord>()
  private readonly _pending = new Map<AssetId, AbortController>()
  private readonly _rootStates = new Map<AssetId, TilesStatus>()
  private readonly _source: IAssetSource
  private readonly _defaults: TilesRuntimeOptions
  private readonly _budget: TileCacheBudget
  private _context?: EngineContext
  private _root?: Group
  private _pipeline?: RenderPipeline
  private _views?: ViewRegistry
  private _log?: ILogger
  private _disposed = false
  private _viewId = 'primary'
  private _activeViewIds: string[] = []
  private _viewAvailable = true
  private _execution = 0
  private _frameExecution: object = {}
  private _lastExecution = -1
  private _cleanupView?: () => void
  private _cleanupPicking?: () => void
  private _cleanupClip?: () => void

  constructor(private readonly _config: TilesPluginOptions) {
    if (_config.viewId !== undefined && _config.viewIds !== undefined)
      throw new TilesError({
        code: 'invalid-input',
        stage: 'root',
        message: 'viewId 与 viewIds 不能同时设置',
      })
    if (
      Array.isArray(_config.viewIds) &&
      (!_config.viewIds.length ||
        _config.viewIds.some((id) => typeof id !== 'string' || !id.trim()) ||
        new Set(_config.viewIds).size !== _config.viewIds.length)
    )
      throw new TilesError({
        code: 'invalid-input',
        stage: 'root',
        message: 'viewIds 必须为非空且不重复的视图 ID',
      })
    this._defaults = resolveTilesRuntimeOptions(_config)
    this._budget = new TileCacheBudget(_config.maxTotalCacheBytes ?? this._defaults.maxCacheBytes)
    this._source = _config.source ?? new HttpAssetSource()
    if (_config.profile !== undefined && _config.profile !== 'tiles11-mesh-preview')
      throw new TilesError({
        code: 'unsupported-capability',
        stage: 'root',
        message: '不支持该 Tiles profile',
      })
    if (_config.contentAdapters.length !== 1)
      throw new TilesError({
        code: 'invalid-input',
        stage: 'root',
        message: 'M0 必须注入一个静态 Mesh 内容适配器',
      })
  }

  onInit(context: EngineContext): void {
    this._context = context
    this._log = context.logger.child('tiles')
    this._root = context.sceneGraph.registerRoot(this.kind)
    context.registerService('TilesService', this)
  }

  onStart(): void {
    const context = this.requireContext()
    this._pipeline = context.getService('RenderPipeline')
    this._views = context.getService('ViewRegistry')
    this._viewId =
      this._config.viewId ??
      (Array.isArray(this._config.viewIds) ? this._config.viewIds[0] : this._views.primaryViewId)
    this._pipeline.addRenderContributor(this)
    if (context.hasService('PickingService')) {
      const picking = context.getService('PickingService')
      this._cleanupPicking = () => picking.removeProvider(this)
      picking.addProvider(this)
    }
    if (context.hasService('ClipService')) {
      const clips = context.getService('ClipService')
      const apply = (volumes: readonly import('@/shared/types/core').ClipVolume[]) => {
        for (const adapter of this._config.contentAdapters) adapter.setClipVolumes?.(volumes)
        this.invalidate()
      }
      apply(clips.volumes)
      const subscription = clips.onChanged(apply)
      this._cleanupClip = () => subscription.dispose()
    }
    const refresh = () => {
      this.refreshViews(this._views?.list() ?? [])
    }
    for (const event of ['view.updated', 'view.removed', 'view.created'] as const)
      context.events.on(event, refresh)
    this._cleanupView = () => {
      for (const event of ['view.updated', 'view.removed', 'view.created'] as const)
        context.events.off(event, refresh)
    }
    refresh()
  }

  async load(request: TilesLoadRequest): Promise<TilesetHandle> {
    const context = this.requireContext()
    const id = request.id ?? nanoid()
    if (this._records.has(id) || this._pending.has(id))
      throw new TilesError({
        code: 'invalid-handle',
        stage: 'root',
        assetId: id,
        message: `Tiles 资产 ID 已存在: ${id}`,
      })
    let url = request.url
    let reference: ReturnType<typeof createTilesReference>
    let transform: Matrix4
    let options: TilesRuntimeOptions
    let auditedBounds: ReturnType<typeof validateSourceBounds> | undefined
    try {
      url = new URL(request.url, typeof document === 'undefined' ? undefined : document.baseURI)
        .href
      reference = createTilesReference(request.coordinate)
      transform = this.matrix(request.transform ?? new Matrix4())
      options = resolveTilesRuntimeOptions(request.options, this._defaults)
      auditedBounds = request.sourceBounds
        ? validateSourceBounds(request.sourceBounds, reference.sourceToRecord)
        : undefined
    } catch (cause) {
      const error = new TilesError(
        {
          code: 'invalid-input',
          stage: 'root',
          assetId: id,
          sourceUrl: url,
          message: cause instanceof Error ? cause.message : String(cause),
        },
        { cause }
      )
      this.setRootStatus(id, 'failed', error)
      context.events.emit('asset.error', { id, kind: this.kind, error })
      throw error
    }
    const startedAt = performance.now()
    const controller = new AbortController()
    const abort = () => controller.abort()
    request.signal?.addEventListener('abort', abort, { once: true })
    if (request.signal?.aborted) abort()
    this._pending.set(id, controller)
    this.setRootStatus(id, 'loading')
    const scheduler = new TileScheduler(options)
    context.events.emit('asset.loading', { id, kind: this.kind })
    try {
      const resource = createResourceContext(url, request)
      const data = await scheduler.schedule(
        'root',
        'download',
        0,
        (signal) =>
          this._source.read(url, resource, { signal, maxBytes: options.maxMetadataBytes }),
        {
          signal: controller.signal,
          retryable: (error) => error instanceof TilesError && error.diagnostic.retryable === true,
        }
      )
      throwIfTilesAborted(controller.signal)
      if (this._disposed || this._pending.get(id) !== controller)
        throw new TilesError({ code: 'cancelled', stage: 'root', message: '根文档加载世代已失效' })
      const classification = classifyTileResource(data)
      if (classification.kind !== 'tileset')
        throw new TilesError({
          code: 'invalid-input',
          stage: 'root',
          sourceUrl: data.url,
          message: '根资源必须是 3D Tiles tileset JSON',
        })
      const parsed = new TilesetJsonParser().parse(
        classification.json,
        { ...resource, documentUrl: data.url },
        { maxDepth: options.maxHierarchyDepth, byteLength: data.data.byteLength }
      )
      const runtime = new TilesRuntime(
        id,
        parsed,
        reference.sourceToRecord,
        this._source,
        this._config.contentAdapters[0],
        options,
        () => this.invalidate(),
        startedAt,
        this._budget,
        this.createPreparationCoordinator(),
        this._config.pipelineDiagnostics
      )
      const bounds = auditedBounds ?? runtime.hierarchy.root.bounds.clone()
      const object3D = new Group()
      object3D.name = request.name ?? id
      object3D.matrixAutoUpdate = false
      object3D.matrix.copy(transform)
      object3D.updateWorldMatrix(true, true, true)
      const handle: TilesetHandle = {
        id,
        kind: this.kind,
        name: request.name ?? id,
        visible: true,
        sourceUrl: data.url,
        version: '1.1',
        coordinate: Object.freeze({ ...request.coordinate }),
        object3D,
        boundingBox: bounds.clone(),
      }
      const rootReadyMs = performance.now() - startedAt
      const record = new TilesetRecord(
        handle,
        bounds,
        runtime,
        rootReadyMs,
        !!auditedBounds,
        parsed.warnings
      )
      if (request.collision) {
        try {
          if (!context.hasService('SpatialQueryService'))
            throw new Error('Tiles 碰撞需要 SpatialQueryService')
          record.collision = new TilesCollisionRuntime(
            handle,
            runtime,
            this._config.contentAdapters[0],
            context.getService('SpatialQueryService'),
            request.collision,
            () => this.invalidate()
          )
        } catch (error) {
          runtime.dispose()
          throw error
        }
      }
      this._records.set(id, record)
      this._rootStates.delete(id)
      context.events.emit('asset.loaded', { id, kind: this.kind })
      this._log?.debug(`根层次就绪，id: ${id}，耗时: ${rootReadyMs.toFixed(1)}ms`)
      return handle
    } catch (error) {
      if (this._pending.get(id) === controller)
        this.setRootStatus(id, controller.signal.aborted ? 'disposed' : 'failed', error)
      if (!controller.signal.aborted)
        context.events.emit('asset.error', {
          id,
          kind: this.kind,
          error: error instanceof Error ? error : new Error(String(error)),
        })
      throw error
    } finally {
      scheduler.dispose()
      request.signal?.removeEventListener('abort', abort)
      if (this._pending.get(id) === controller) this._pending.delete(id)
    }
  }

  mount(handle: TilesetHandle): void {
    this.requireContext()
    const record = this.record(handle.id)
    if (record.handle !== handle || handle.kind !== 'tiles')
      throw new TilesError({
        code: 'invalid-handle',
        stage: 'runtime',
        message: '句柄不属于当前 TilesPlugin 或已被替换',
      })
    if (record.mounted) return
    this._root!.add(handle.object3D)
    record.mounted = true
    record.runtime.markMounted()
    this._context!.sceneBounds.register({
      id: handle.id,
      kind: this.kind,
      object3D: handle.object3D,
      getLocalBoundingBox: () => record.localBounds.clone(),
      isVisible: () => handle.visible,
    })
    this.activate(record)
    this._context!.events.emit('asset.mounted', { id: handle.id, kind: this.kind })
  }

  unmount(id: AssetId): void {
    const record = this.record(id)
    if (!record.mounted) return
    record.mounted = false
    this.activate(record)
    record.handle.object3D.removeFromParent()
    this._context!.sceneBounds.unregister(this.kind, id)
    this._context!.events.emit('asset.unmounted', { id, kind: this.kind })
  }

  getAsset(id: AssetId): TilesetHandle | undefined {
    return this._records.get(id)?.handle
  }
  getAll(): TilesetHandle[] {
    return [...this._records.values()].map((record) => record.handle)
  }

  getStatus(id: AssetId, viewId = this._viewId): TilesStatus | undefined {
    const record = this._records.get(id)
    if (!record) return this._rootStates.get(id)
    const status = record.runtime.getStatus(viewId)
    return Object.freeze({
      ...status,
      id,
      root: 'ready',
      mounted: record.mounted,
      visible: record.handle.visible,
      viewId,
      quality:
        status.quality === 'ready' && !record.hasSourceBounds
          ? 'unverified-bounds'
          : status.quality,
      rootReadyMs: record.rootReadyMs,
    })
  }

  getViewStatuses(id: AssetId): readonly TilesStatus[] {
    return Object.freeze(
      this._activeViewIds.flatMap((viewId) => {
        const status = this.getStatus(id, viewId)
        return status ? [status] : []
      })
    )
  }

  setVisibility(id: AssetId, visible: boolean): void {
    const record = this.record(id)
    record.handle.visible = visible
    record.handle.object3D.visible = visible
    this.activate(record)
    this._context!.events.emit('asset.visibilityChanged', { id, visible })
  }

  /** 设置 tileset 的外观，并让当前及后续流式瓦片沿用该设置。 */
  setAppearance(id: AssetId, appearance?: TilesAppearance): void {
    this.record(id)
    const adapter = this._config.contentAdapters[0]
    if (!adapter.setAppearance) {
      throw new TilesError({
        code: 'unsupported-capability',
        stage: 'runtime',
        assetId: id,
        message: '当前 Tiles 内容适配器不支持外观设置',
      })
    }
    adapter.setAppearance(id, appearance)
    this.invalidate()
  }

  setTransform(id: AssetId, value: Matrix4 | readonly number[]): void {
    const record = this.record(id)
    record.handle.object3D.matrix.copy(this.matrix(value))
    record.handle.object3D.updateWorldMatrix(true, true, true)
    record.collision?.invalidateTransform()
    this.invalidate()
  }

  configure(id: AssetId, options: Partial<TilesRuntimeOptions>): void {
    this.record(id).runtime.configure(options)
  }

  configureAdaptiveBudget(id: AssetId, options: TilesAdaptiveBudgetOptions | false): void {
    this.record(id).runtime.configureAdaptiveBudget(options)
  }

  retryFailed(id: AssetId): void {
    const record = this.record(id)
    record.runtime.retryFailed()
    record.collision?.retryFailed()
  }

  configureBudget(maxTotalCacheBytes: number): void {
    this.requireContext()
    const previous = this._budget.getStats().maxBytes
    this._budget.configure(maxTotalCacheBytes)
    if (previous === maxTotalCacheBytes) return
    for (const record of this._records.values()) record.runtime.notifyBudgetLimitChanged()
    this.invalidate()
  }

  configureMemoryLimit(limit: TilesMemoryBytes | false): void {
    this.requireContext()
    this._budget.configureMemoryLimit(limit)
    for (const record of this._records.values()) record.runtime.notifyBudgetLimitChanged()
    this.invalidate()
  }

  getMemoryStatus(): TilesMemoryStatus {
    return Object.freeze(this._budget.getMemoryStats())
  }

  trimCache(targetBytes: number, minIdleMs: number, maxEntries?: number): number {
    this.requireContext()
    const freed = this._budget.trimToTarget(targetBytes, minIdleMs, maxEntries)
    if (freed > 0) this.invalidate()
    return freed
  }

  getBudgetStatus(): Readonly<{
    maxBytes: number
    totalBytes: number
    protectedBytes: number
    reservedBytes: number
  }> {
    return Object.freeze(this._budget.getStats())
  }

  onUpdateFrame(frame: EngineFrameContext): void {
    this._execution++
    this._frameExecution = frame
    this.refreshViews(frame.views)
  }

  getPipelineStatus(id: AssetId) {
    return this._records.get(id)?.runtime.getPipelineStatus()
  }

  private refreshViews(views: readonly import('@/shared/types/view').ViewSnapshot[]): void {
    const selected = this._config.viewIds ?? [this._viewId]
    this._activeViewIds = views
      .filter(
        (view) =>
          (selected === 'all' || selected.includes(view.id)) &&
          view.visible &&
          view.rect.width > 0 &&
          view.rect.height > 0
      )
      .map((view) => view.id)
    this._viewAvailable = this._activeViewIds.length > 0
    for (const record of this._records.values()) {
      record.runtime.retainViews(this._activeViewIds)
      this.activate(record)
    }
  }

  onRenderView(frame: ViewFrameContext): void {
    if (this._lastExecution === this._execution) return
    // 兼容直接驱动旧钩子的单 view 宿主；标准 RenderLoop 使用全视图准备阶段。
    this.onViewsPrepared([frame], [frame.viewId])
  }

  onViewsPrepared(frames: readonly ViewFrameContext[], drawViewIds: readonly string[]): void {
    if (this._disposed) return
    const participating = frames.filter((frame) => this._activeViewIds.includes(frame.viewId))
    this._lastExecution = this._execution
    const preparation = this.createPreparationCoordinator()
    // 先完成上一帧等待结束的准备，选择器本帧即可使用回执；前后两次共用同一额度。
    preparation.run(this._frameExecution)
    const budgetBefore = this._budget.getStats()
    const memoryBefore = this._budget.getMemoryStats().committed
    for (const record of this._records.values()) {
      record.handle.object3D.updateWorldMatrix(true, false)
      record.collision?.update()
      const prepareContext = this.createPrepareContext(participating[0])
      record.runtime.updateViews(
        (record.mounted && record.handle.visible ? participating : []).map((frame) => {
          const camera = frame.camera as PerspectiveCamera | OrthographicCamera
          camera.updateWorldMatrix(true, false)
          const frustum = new Frustum().setFromProjectionMatrix(
            new Matrix4().multiplyMatrices(camera.projectionMatrix, camera.matrixWorldInverse)
          )
          return {
            viewId: frame.viewId,
            executionId: this._execution,
            frustum,
            cameraPosition: new Vector3().setFromMatrixPosition(camera.matrixWorld),
            projectionMatrix: camera.projectionMatrix.clone(),
            perspective: (camera as PerspectiveCamera).isPerspectiveCamera === true,
            cssHeight: frame.viewRect.height,
            placement: record.handle.object3D.matrixWorld.clone(),
          }
        }),
        prepareContext,
        drawViewIds
      )
    }
    const budgetAfter = this._budget.getStats()
    const memoryAfter = this._budget.getMemoryStats().committed
    preparation.run(this._frameExecution)
    if (
      budgetAfter.protectedBytes + budgetAfter.reservedBytes <
        budgetBefore.protectedBytes + budgetBefore.reservedBytes ||
      memoryAfter.cpuBytes < memoryBefore.cpuBytes ||
      memoryAfter.gpuBytes < memoryBefore.gpuBytes
    )
      for (const record of this._records.values()) record.runtime.notifyBudgetCapacityChanged()
  }

  render(frame: ViewFrameContext, phase: RenderPhase, context: PhaseRenderContext): void {
    if (this._disposed || !this._activeViewIds.includes(frame.viewId)) return
    for (const record of this._records.values())
      if (record.mounted && record.handle.visible) record.runtime.render(frame, phase, context)
  }

  pick(query: PickQuery): PickResult | null {
    if (this._disposed || !this._viewAvailable || (query.kinds && !query.kinds.includes(this.kind)))
      return null
    const viewId = query.viewId ?? this._viewId
    if (!this._activeViewIds.includes(viewId)) return null
    let nearest: PickResult | null = null
    for (const record of this._records.values()) {
      if (!record.mounted || !record.handle.visible || !record.handle.object3D.visible) continue
      if (query.assetIds && !query.assetIds.includes(record.handle.id)) continue
      const hit = record.runtime.pick({ ...query, viewId })
      if (hit && (!nearest || hit.distance < nearest.distance)) nearest = hit
    }
    return nearest
  }

  getRenderActivity(context: RenderActivityContext): RenderActivityReport {
    return {
      needsNextFrame:
        !this._disposed &&
        this._viewAvailable &&
        context.renderedViewIds.some((id) => this._activeViewIds.includes(id)) &&
        [...this._records.values()].some((record) => record.runtime.hasImmediateWork),
    }
  }

  dispose(id?: AssetId): void {
    let released = false
    const ids =
      id === undefined ? new Set([...this._pending.keys(), ...this._records.keys()]) : [id]
    for (const assetId of ids) {
      if (this._pending.has(assetId) || this._records.has(assetId))
        this.setRootStatus(assetId, 'disposed')
      this._pending.get(assetId)?.abort()
      this._pending.delete(assetId)
      const record = this._records.get(assetId)
      if (!record) continue
      this._records.delete(assetId)
      record.collision?.dispose()
      record.runtime.dispose()
      released = true
      this._context?.sceneBounds.unregister(this.kind, assetId)
      record.handle.object3D.removeFromParent()
      this._context?.events.emit('asset.disposed', { id: assetId, kind: this.kind })
    }
    // 共享容量释放后，其他 Record 在下一帧重新评估候选，无需相机再移动。
    if (released) this.invalidate()
  }

  onDestroy(): void {
    if (this._disposed) return
    this._disposed = true
    for (const cleanup of [this._cleanupPicking, this._cleanupClip]) {
      try {
        cleanup?.()
      } catch (error) {
        this._log?.warn(`解除 Tiles 工具失败: ${String(error)}`)
      }
    }
    this._cleanupView?.()
    try {
      this._pipeline?.removeRenderContributor(this)
    } catch (error) {
      this._log?.warn(`移除 Tiles 绘制贡献者失败: ${String(error)}`)
    }
    this.dispose()
    if (this._config.ownsContentAdapters !== false)
      for (const adapter of this._config.contentAdapters) {
        try {
          adapter.dispose?.()
        } catch (error) {
          this._log?.warn(`释放 Tiles 适配器失败: ${String(error)}`)
        }
      }
    this._root?.removeFromParent()
  }

  private activate(record: TilesetRecord): void {
    const collisionActive =
      record.mounted &&
      !!record.collision?.hasDemand &&
      (record.handle.visible || record.collision.options.ownerStatePolicy === 'always')
    record.collision?.setActive(collisionActive)
    record.runtime.setActive(
      (record.mounted && record.handle.visible && this._viewAvailable) || collisionActive
    )
  }

  setCollisionInterest(
    id: AssetId,
    consumerId: string,
    interest: TilesCollisionInterest
  ): TilesCollisionCoverage {
    const record = this.record(id)
    if (!record.collision) throw new Error('该 Tiles 资产未启用碰撞')
    const coverage = record.collision.setInterest(consumerId, interest)
    this.activate(record)
    return coverage
  }
  removeCollisionInterest(id: AssetId, consumerId: string): void {
    const record = this.record(id)
    record.collision?.removeInterest(consumerId)
    this.activate(record)
  }
  getCollisionCoverage(id: AssetId, consumerId: string): TilesCollisionCoverage | undefined {
    return this._records.get(id)?.collision?.getCoverage(consumerId)
  }

  private setRootStatus(id: AssetId, root: TilesStatus['root'], error?: unknown): void {
    this._rootStates.delete(id)
    this._rootStates.set(
      id,
      Object.freeze({
        id,
        root,
        mounted: false,
        visible: false,
        viewId: this._viewId,
        quality: root === 'loading' ? 'loading' : root === 'failed' ? 'degraded' : 'inactive',
        hasCoverage: false,
        displayedContents: 0,
        residentContents: 0,
        pendingRequests: root === 'loading' ? 1 : 0,
        pendingDecodes: 0,
        pendingPreparation: 0,
        hierarchyPending: 0,
        residentBytes: 0,
        transientBytes: 0,
        metadataBytes: 0,
        budgetExceeded: false,
        memoryOverBudget: false,
        budgetLimitedAdmission: false,
        budgetLimitedSelection: false,
        maxCacheBytes: this._defaults.maxCacheBytes,
        protectedBytes: 0,
        reservedBytes: 0,
        fallbackBytes: 0,
        stoppedRefinements: 0,
        budgetPressure: Object.freeze({
          reason: 'none',
          requiredBytes: 0,
          localAvailableBytes: this._defaults.maxCacheBytes,
          sharedAvailableBytes: null,
        }),
        adaptiveBudget: false,
        diagnostics: Object.freeze(
          error === undefined ? [] : [toTilesDiagnostic(error, { assetId: id, stage: 'root' })]
        ),
      })
    )
    if (this._rootStates.size > 100) this._rootStates.delete(this._rootStates.keys().next().value!)
  }
  private invalidate(): void {
    if (!this._disposed && !this._viewAvailable)
      this._context?.renderInvalidation.invalidate('tiles.collision')
    if (!this._disposed && this._viewAvailable)
      for (const viewId of this._activeViewIds)
        this._context?.renderInvalidation.invalidate('tiles.content', { viewId })
  }
  private createPreparationCoordinator(): TilePreparationCoordinator {
    const context = this.requireContext()
    if (context.graphics.kind === 'webgl2') {
      if (!context.renderer)
        throw new TilesError({
          code: 'unsupported-capability',
          stage: 'runtime',
          message: 'Tiles WebGL2 缺少 legacy renderer',
        })
      return TilePreparationCoordinator.forRenderer(context.renderer, this._config.preparation)
    }
    return TilePreparationCoordinator.forGraphics(context.graphics, this._config.preparation)
  }
  private createPrepareContext(frame?: ViewFrameContext): TileContentPrepareContext {
    const context = this.requireContext()
    const graphics = frame?.graphics ?? context.graphics
    if (graphics.kind !== 'webgl2') return { graphics }
    return {
      graphics,
      legacyRenderer: frame?.renderer ?? context.renderer,
    }
  }
  private requireContext(): EngineContext {
    if (this._disposed || !this._context)
      throw new TilesError({
        code: 'disposed',
        stage: 'runtime',
        message: 'TilesPlugin 尚未初始化或已销毁',
      })
    return this._context
  }
  private record(id: AssetId): TilesetRecord {
    this.requireContext()
    const record = this._records.get(id)
    if (!record)
      throw new TilesError({
        code: 'invalid-handle',
        stage: 'runtime',
        assetId: id,
        message: `Tiles 资产不存在: ${id}`,
      })
    return record
  }
  private matrix(value: Matrix4 | readonly number[]): Matrix4 {
    if (!(value instanceof Matrix4) && value.length !== 16)
      throw new TilesError({
        code: 'invalid-input',
        stage: 'runtime',
        message: '宿主变换须包含16个数值',
      })
    const matrix = value instanceof Matrix4 ? value.clone() : new Matrix4().fromArray(value)
    validateTilesTransform(matrix)
    return matrix
  }
}
